import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getRepayments, initiateRepayment } from "@/api";
import { openRazorpayCheckout, loadRazorpayScript } from "@/utils/razorpay";
import BottomNav from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  ArrowLeft, Loader2, IndianRupee, Calendar, CheckCircle, Clock, 
  AlertTriangle, CreditCard, Filter, ChevronDown, Banknote 
} from "lucide-react";

export default function CustomerRepayments() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [repayments, setRepayments] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => { 
    loadRepayments(); 
    // Preload Razorpay checkout script to prevent "Payment gateway not loaded" error
    loadRazorpayScript().catch(() => {});
  }, []);

  const loadRepayments = async () => {
    try {
      const res = await getRepayments();
      setRepayments(res.data.repayments || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  const handlePay = async (repaymentId) => {
    setPaying(repaymentId);
    try {
      const res = await initiateRepayment(repaymentId);
      
      if (!res.data.success) {
        toast.error("Failed to create payment order");
        setPaying(null);
        return;
      }
      
      const { order_id, amount, amount_paise, currency, key_id, repayment_id } = res.data;
      
      await openRazorpayCheckout({
        order_id,
        amount: amount_paise,
        currency,
        key_id,
        name: 'Rebuilt',
        description: `Repayment - ₹${amount.toFixed(2)}`,
        prefill_contact: user?.mobile,
        prefill_email: `${user?.mobile || "user"}@rebuilt.in`,
        onSuccess: async (response) => {
          toast.success("Payment successful!");
          setPaying(null);
          loadRepayments();
        },
        onFailure: (error) => {
          toast.error(error.description || "Payment failed. Please try again.");
          setPaying(null);
        }
      });
      
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed");
      setPaying(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return dateStr.substring(0, 10); }
  };

  const getStatusConfig = (status) => {
    const configs = {
      paid: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-100 text-green-700', label: 'Paid' },
      overdue: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', label: 'Overdue' },
      pending: { icon: Clock, color: 'text-amber-600', bg: 'bg-white', border: 'border-gray-200', badge: 'bg-amber-100 text-amber-700', label: 'Pending' },
    };
    return configs[status] || configs.pending;
  };

  // Filtered repayments
  const filteredRepayments = activeFilter === 'all' 
    ? repayments 
    : repayments.filter(r => r.status === activeFilter);

  // Stats
  const totalDue = parseFloat(summary.total_due || 0);
  const totalPaid = parseFloat(summary.total_paid || 0);
  const totalOverdue = parseFloat(summary.total_overdue || 0);
  const totalPending = Math.max(0, totalDue - totalPaid - totalOverdue);
  const progressPercent = totalDue > 0 ? Math.min(100, Math.round((totalPaid / totalDue) * 100)) : 0;

  const filterTabs = [
    { value: 'all', label: 'All', count: repayments.length },
    { value: 'pending', label: 'Pending', count: repayments.filter(r => r.status === 'pending').length },
    { value: 'overdue', label: 'Overdue', count: repayments.filter(r => r.status === 'overdue').length },
    { value: 'paid', label: 'Paid', count: repayments.filter(r => r.status === 'paid').length },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-24" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-50"
        style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1565C0 100%)' }}
      >
        <div className="max-w-lg mx-auto px-5 py-4 flex items-center gap-3">
          <button onClick={() => navigate("/customer/dashboard")} className="text-white/70 hover:text-white transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white tracking-tight" style={{fontFamily:'Poppins, sans-serif'}}>My Repayments</h1>
            <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-medium">Payment Schedule</p>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-5 py-6 space-y-5">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm text-gray-500">Loading repayments...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Progress Card */}
            {totalDue > 0 && (
              <div 
                className="rounded-2xl p-6 text-white relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #1565C0 0%, #00BCD4 100%)' }}
              >
                <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
                <div className="absolute -left-4 -bottom-4 w-20 h-20 rounded-full bg-white/10" />
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-white/70 text-xs uppercase tracking-wider font-semibold">Repayment Progress</p>
                    <span className="text-2xl font-black">{progressPercent}%</span>
                  </div>
                  <div className="h-3 bg-white/20 rounded-full overflow-hidden mb-4">
                    <div 
                      className="h-full rounded-full transition-all duration-700 ease-out"
                      style={{ 
                        width: `${progressPercent}%`,
                        background: 'linear-gradient(90deg, #4DD0E1, #00E676)'
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-sm">
                    <div>
                      <p className="text-white/60 text-[10px] uppercase tracking-wider">Paid</p>
                      <p className="font-bold">{formatCurrency(totalPaid)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-white/60 text-[10px] uppercase tracking-wider">Remaining</p>
                      <p className="font-bold">{formatCurrency(totalPending + totalOverdue)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white/60 text-[10px] uppercase tracking-wider">Total</p>
                      <p className="font-bold">{formatCurrency(totalDue)}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-center">
                <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center mx-auto mb-2">
                  <Clock className="h-4 w-4 text-amber-500" />
                </div>
                <p className="text-lg font-bold text-gray-900">{formatCurrency(totalPending)}</p>
                <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Pending</p>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-center">
                <div className="w-8 h-8 rounded-xl bg-green-50 flex items-center justify-center mx-auto mb-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                </div>
                <p className="text-lg font-bold text-gray-900">{formatCurrency(totalPaid)}</p>
                <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Paid</p>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-center">
                <div className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center mx-auto mb-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                </div>
                <p className="text-lg font-bold text-gray-900">{formatCurrency(totalOverdue)}</p>
                <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Overdue</p>
              </div>
            </div>

            {/* Next Due Alert */}
            {summary.next_due_date && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-blue-600 font-semibold uppercase tracking-wider">Next Payment Due</p>
                  <p className="text-sm font-bold text-blue-900">{formatDate(summary.next_due_date)} — {formatCurrency(summary.next_due_amount)}</p>
                </div>
              </div>
            )}

            {/* Filter Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {filterTabs.map(tab => (
                <button
                  key={tab.value}
                  onClick={() => setActiveFilter(tab.value)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all border ${
                    activeFilter === tab.value
                      ? 'bg-[#1565C0] text-white border-[#1565C0] shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                      activeFilter === tab.value ? 'bg-white/20' : 'bg-gray-100'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Repayments List */}
            {filteredRepayments.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Banknote className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="font-semibold text-gray-700 text-base">
                  {repayments.length === 0 ? 'No Repayments Yet' : `No ${activeFilter} repayments`}
                </h3>
                <p className="text-gray-500 text-sm mt-2 max-w-xs mx-auto">
                  {repayments.length === 0 
                    ? 'Your repayment schedule will appear here once funds are disbursed.'
                    : 'Try a different filter to see more repayments.'
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredRepayments.map((r, idx) => {
                  const config = getStatusConfig(r.status);
                  const StatusIcon = config.icon;
                  const isOverdue = r.status === 'overdue';
                  const isPending = r.status === 'pending';
                  const isPaid = r.status === 'paid';
                  
                  return (
                    <div 
                      key={r.id} 
                      className={`rounded-2xl border p-5 transition-all hover:shadow-md ${config.bg} ${config.border}`}
                      style={{ animationDelay: `${idx * 50}ms` }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 rounded-xl ${config.bg} border ${config.border} flex items-center justify-center`}>
                            <StatusIcon className={`h-5 w-5 ${config.color}`} />
                          </div>
                          <div>
                            <div className="font-bold text-gray-900 text-lg">
                              {formatCurrency(r.amount)}
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
                              <Calendar className="h-3 w-3" />
                              Due: {formatDate(r.due_date)}
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right flex flex-col items-end gap-2">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${config.badge}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              isPaid ? 'bg-green-500' : isOverdue ? 'bg-red-500' : 'bg-amber-500'
                            }`} />
                            {config.label}
                          </span>
                          
                          {(isPending || isOverdue) && (
                            <button 
                              onClick={() => handlePay(r.id)} 
                              disabled={paying === r.id}
                              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-white shadow-sm hover:shadow-md transition-all disabled:opacity-50 ${
                                isOverdue 
                                  ? 'bg-gradient-to-r from-red-500 to-red-600'
                                  : 'bg-gradient-to-r from-[#1565C0] to-[#0D47A1]'
                              }`}
                            >
                              {paying === r.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <>
                                  <CreditCard className="h-3 w-3" />
                                  Pay Now
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Application info */}
                      {r.application && (
                        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-400">
                          <IndianRupee className="h-3 w-3" />
                          <span>{r.application.application_number || ''}</span>
                          {r.application.lender_name && (
                            <>
                              <span className="text-gray-300">•</span>
                              <span>{r.application.lender_name}</span>
                            </>
                          )}
                        </div>
                      )}
                      
                      {/* Paid date */}
                      {r.paid_at && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-green-600">
                          <CheckCircle className="h-3 w-3" />
                          Paid on {formatDate(r.paid_at)}
                          {r.payment_reference && (
                            <span className="ml-2 text-gray-400">Ref: {r.payment_reference}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
